import React from 'react';
import { observer } from 'mobx-react';

import useStores from '@stores/useStores';
import { FeatureGateBadge } from './featureGateBadge';
import { useUpgradeNavigation } from '../../common/useUpgradeNavigation';
import { ServerFeatures } from '@stores/features';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { Flex } from '@ui-library/flex';
import { TrialBaseBadge } from '@components/trial/common/trialBaseBadge';
import { buildDefaultFeatureCTAs, featuresRegistry } from '../../featuresRegistry';

interface TrialFeatureTagProps {
  featureKey?: keyof ServerFeatures;
  featureName?: string;
  compact?: boolean;
  variant?: 'default' | 'daysRemaining';
}

export const FeatureTrialTag: React.FC<TrialFeatureTagProps> = observer(
  ({ featureKey, featureName: featureNameProp, compact, variant = 'default', children }) => {
    const { trialStore } = useStores();
    const isPLGTrialEnabled = experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrial);
    // Resolve trial info ahead of the hook so the hook receives a stable, trialType-aware
    // ctaText. `getFeatureTrialInfo` is a plain store accessor, not a hook — safe to call here.
    const trialInfo = featureKey ? trialStore.getFeatureTrialInfo(featureKey) : null;
    const trialType = trialInfo?.trial.definition.trialType;
    const onUpgrade = useUpgradeNavigation({
      ctaLocation: 'feature_gate',
      ctaText: trialType === 'addon' ? 'Buy Add-on' : 'Upgrade',
      destinationPage: 'plans_page',
      triggerContext: featureKey,
    });
    if (!isPLGTrialEnabled || !trialInfo) {
      return children ? <>{children}</> : null;
    }

    const isTrialActive = trialInfo.trial.status === 'in_progress';

    const registryEntry = featureKey ? featuresRegistry[featureKey] : undefined;
    const featureName = featureNameProp ?? registryEntry?.name ?? 'This feature';
    const targetPlanName = trialInfo.trial.definition.plan?.displayName ?? '';

    const defaultTrialBadge = buildDefaultFeatureCTAs({
      featureName,
      currentPlan: '',
      targetPlan: targetPlanName,
      currentLimit: 0,
    }).trialBadge;

    const trialBadgeDescription = registryEntry?.ctas?.trialBadge ?? defaultTrialBadge;
    const badge =
      variant === 'daysRemaining' ? (
        <TrialBaseBadge
          label={`Trial - ${trialStore.trialDaysRemaining}d left`}
          trialPhase={trialInfo.phase}
          compact={compact}
        />
      ) : (
        <FeatureGateBadge
          compact={compact}
          isTrialActive={isTrialActive}
          daysRemaining={trialInfo.daysRemaining}
          trialPhase={trialInfo.phase}
          trialType={trialInfo.trial.definition.trialType}
          targetPlanName={targetPlanName}
          description={trialBadgeDescription}
          onUpgrade={onUpgrade}
        />
      );

    return (
      <Flex gap="6px" alignItemsCenter>
        {children}
        {badge}
      </Flex>
    );
  },
);
